import {
  InvitationFindByTokenResponse,
  InvitationStatus,
  type InvitationWithDetails,
} from '@avanzu/types';
import GoogleIcon from '@mui/icons-material/Google';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import OrganisationCard from '../../components/OrganisationCard';
import SadAlert from '../../components/SadAlert';
import { useAuth } from '../../contexts/AuthContext';
import getRoleLabel from '../../helpers/getRoleLabel';
import SignupForm, { SignupFormRef, signupSchema } from './components/SignupForm';

type SignupFormData = yup.InferType<typeof signupSchema>;

interface JoinOrganisationProps {}

const JoinOrganisation: React.FC<JoinOrganisationProps> = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invitation, setInvitation] = useState<InvitationWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const signupFormRef = useRef<SignupFormRef>(null);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/invitations/${token}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Invitation not found or has expired');
          } else if (response.status === 410) {
            throw new Error('This invitation has already been used');
          } else {
            throw new Error('Unable to load invitation');
          }
        }

        const invitation: InvitationFindByTokenResponse = await response.json();

        // Additional client-side validation
        if (invitation.status !== InvitationStatus.PENDING) {
          throw new Error('This invitation is no longer valid');
        }

        if (new Date(invitation.expiresAt) < new Date()) {
          throw new Error('This invitation has expired');
        }

        setInvitation(invitation);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleGoogleLogin = () => {
    if (!token) return;

    // Redirect to Google OAuth with invitation token
    const authUrl = `${process.env.REACT_APP_API_URL}/auth/oauth/google?action=signup&invitationToken=${token}&returnUrl=${encodeURIComponent('/')}`;
    window.location.href = authUrl;
  };

  const handleXeroLogin = () => {
    if (!token) return;

    // Redirect to Xero OAuth with invitation token
    const authUrl = `${process.env.REACT_APP_API_URL}/auth/oauth/xero??action=signup&invitationToken=${token}&returnUrl=${encodeURIComponent('/')}`;
    window.location.href = authUrl;
  };

  const handleDirectAccept = async () => {
    if (!user || !invitation || !token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/invitations/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Invalid request');
        } else if (response.status === 409) {
          throw new Error('You are already a member of this organisation');
        } else {
          throw new Error('Failed to join organisation');
        }
      }

      // Success - redirect to organisation select
      navigate('/select-organisation');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join organisation');
    }
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    try {
      // Include the invitation token in the signup request
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          invitationToken: token,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Signup failed');
      }

      const result = await response.json();

      // The signup should automatically accept the invitation and redirect
      // Navigate to the return URL or dashboard
      navigate(result.returnUrl || '/');
    } catch (err) {
      signupFormRef.current?.reset();
      setError(err instanceof Error ? err.message : 'Failed to create account');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading invitation...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !invitation) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" align="center" gutterBottom color="error">
              Invalid Invitation
            </Typography>
            <SadAlert sx={{ mb: 3 }}>
              {error || 'This invitation link is invalid or has expired.'}
            </SadAlert>
            <Button fullWidth variant="contained" onClick={() => navigate('/')}>
              Go to app home
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // If user is not logged in, show login options
  if (!user) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" align="center" gutterBottom>
              Join {invitation.organisationName}
            </Typography>

            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
              You've been invited by <strong>{invitation.invitedByName}</strong> to join their
              organisation as a <strong>{getRoleLabel(invitation.role)}</strong>.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Please sign in or create an account to accept this invitation.
            </Alert>

            {error && <SadAlert sx={{ mb: 3 }}>{error}</SadAlert>}

            <Container maxWidth="xs">
              <Box sx={{ mr: 2, ml: 2 }}>
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<GoogleIcon />}
                    onClick={handleGoogleLogin}
                    sx={{
                      bgcolor: '#4285F4',
                      '&:hover': { bgcolor: '#357ae8' },
                    }}
                  >
                    Continue with Google
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleXeroLogin}
                    sx={{
                      bgcolor: '#13B5EA',
                      '&:hover': { bgcolor: '#0e8bb5' },
                    }}
                  >
                    Continue with Xero
                  </Button>
                </Stack>

                <Divider sx={{ my: 3, color: 'text.secondary' }}>OR</Divider>

                <SignupForm
                  onSubmit={handleSignupSubmit}
                  isLoading={loading}
                  submitButtonText="Create Account & Join Organisation"
                  invitationToken={token}
                />
              </Box>
            </Container>
          </Paper>
        </Box>
      </Container>
    );
  }

  // User is logged in - check if email matches invitation
  if (user.email.toLowerCase() !== invitation.email.toLowerCase()) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" align="center" gutterBottom color="error">
              Email Mismatch
            </Typography>
            <SadAlert sx={{ mb: 3 }}>
              This invitation was sent to <strong>{invitation.email}</strong>, but you're signed in
              as <strong>{user.email}</strong>. Please sign in with the correct account or contact
              the person who invited you.
            </SadAlert>
            <Stack spacing={2}>
              <Button fullWidth variant="contained" onClick={() => navigate('/account/logout')}>
                Sign Out & Try Again
              </Button>
              <Button fullWidth variant="outlined" onClick={() => navigate('/')}>
                Go to Dashboard
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Container>
    );
  }

  // User is logged in with correct email - show organisation details and join confirmation
  const organisationForCard = {
    id: invitation.organisationId,
    name: invitation.organisationName,
    role: invitation.role,
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Fade in={true}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 600 }}>
            <Typography variant="h4" align="center" gutterBottom>
              Join Organisation
            </Typography>

            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Hello{' '}
              <strong>
                {user.firstName} {user.lastName}
              </strong>
              ! You've been invited to join this organisation by{' '}
              <strong>{invitation.invitedByName}</strong>.
            </Typography>

            {error && <SadAlert sx={{ mb: 3 }}>{error}</SadAlert>}

            <Box sx={{ mb: 4 }}>
              <OrganisationCard
                organisation={organisationForCard}
                isSelected={true}
                onSelect={() => {}} // No-op since it's always selected in this context
              />
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              You will be added as a <strong>{getRoleLabel(invitation.role)}</strong> to this organisation.
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" onClick={handleDirectAccept} size="large">
                Accept Invitation
              </Button>
              <Button variant="outlined" onClick={() => navigate('/')} size="large">
                Maybe Later
              </Button>
            </Stack>
          </Paper>
        </Fade>
      </Box>
    </Container>
  );
};

export default JoinOrganisation;
